<template>
  <div class="public-api-token-form pa-10">

    <title-page icon="mdi-key" class="mb-0">
      {{ isEdit ? $t('public-api-token.edit') : $t('public-api-token.add') }}
    </title-page>

    <div class="mt-5">
      <!-- Token info banner (edit mode only) -->
      <v-alert
        v-if="isEdit && tokenMasked"
        type="info"
        outlined
        dense
        class="mb-6"
      >
        <div class="d-flex align-center">
          <v-icon left color="info">mdi-key-variant</v-icon>
          <span class="mr-3">{{ $t('public-api-token.token') }}:</span>
          <code class="token-code">{{ tokenMasked }}</code>
        </div>
      </v-alert>

      <headline>{{ $t('public-api-token.token-details') }}</headline>
      <v-form ref="form" class="form-grid" @submit.prevent="save">
        <v-row>
          <v-col cols="12" md="6">
            <form-field
              :label="$t('public-api-token.name')"
              v-model="form.name"
              :rules="[commonRules.required]"
            />
          </v-col>
          <v-col cols="12" md="6" v-if="isEdit">
            <form-field
              :label="$t('public-api-token.enabled')"
              v-model="form.enabled"
              type="switch"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <form-field
              type="textarea"
              :label="$t('public-api-token.description')"
              v-model="form.description"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <form-field
              type="organization-select"
              v-model="form.organizationIds"
              selection-with-no-extra
              chips
              :label="$t('public-api-token.organizations')"
              multiple
              clearable
              without-filter
            />
          </v-col>
        </v-row>

        <v-row class="mt-6">
          <v-col cols="12" class="text-center">
            <v-btn type="submit" color="primary">
              {{ $t('save') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>

      <template v-if="isEdit">
        <v-divider class="my-8"></v-divider>

        <v-row>
          <v-col class="text-center">
            <v-btn small outlined color="warning" class="mr-4" @click="regenerateToken">
              <v-icon left small>mdi-refresh</v-icon>
              {{ $t('public-api-token.regenerate') }}
            </v-btn>
            <v-btn small outlined color="error" @click="deleteItem">
              <v-icon left small>mdi-delete</v-icon>
              {{ $t('delete') }}
            </v-btn>
          </v-col>
        </v-row>
      </template>
    </div>

    <!-- Token Display Modal -->
    <v-dialog v-model="showTokenModal" max-width="600" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ isRegenerate ? $t('public-api-token.token-regenerated') : $t('public-api-token.token-created') }}
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" outlined dense class="mb-4">
            {{ $t('public-api-token.token-warning') }}
          </v-alert>

          <v-row dense>
            <v-col>
              <label class="text-subtitle-2">{{ $t('public-api-token.your-token') }}</label>
              <v-text-field
                v-model="createdToken"
                readonly
                outlined
                dense
                class="token-field mt-2"
              >
                <template #append>
                  <v-btn icon small @click="copyToClipboard">
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col class="text-center">
              <v-btn color="primary" @click="closeTokenModal">
                {{ $t('public-api-token.understood') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import RulesMixin from "@mixins/RulesMixin"
import PublicApiTokenRepository from "@repository/PublicApiTokenRepository"

export default {
  components: {
  },

  mixins: [RulesMixin],

  data() {
    return {
      form: {
        name: '',
        description: '',
        organizationIds: [],
        enabled: true,
      },
      tokenMasked: '',
      showTokenModal: false,
      createdToken: '',
      isRegenerate: false,
    }
  },

  mounted() {
    if (!this.currentUser.isZE || !this.currentUser.permissions.includes('OPERATOR_WRITE')) {
      this.$router.replace({name: 'dashboard'})
      return
    }

    if (this.isEdit) {
      this.loadData()
    }
  },

  watch: {
    form: {
      handler() {
        this.setUnsavedChanges()
      },
      deep: true
    }
  },

  computed: {
    id() {
      return this.$route.params.id ?? null
    },

    isEdit() {
      return !!this.id
    }
  },

  methods: {
    loadData() {
      this.showLoading(true)
      PublicApiTokenRepository.get(this.id).then(result => {
        this.tokenMasked = result.tokenMasked || ''
        this.form = {
          name: result.name,
          description: result.description || '',
          organizationIds: result.organizationIds || [],
          enabled: result.enabled !== false,
        }
        this.showLoading(false)
        this.$nextTick(() => {
          this.cleanUnsavedChanges()
        })
      }).catch(err => {
        this.showLoading(false)
        this.notifyError(err)
        this.$router.replace({name: 'public-api-tokens'})
      })
    },

    save() {
      if (this.$refs.form.validate()) {
        this.showLoading(true)

        const payload = {
          name: this.form.name,
          description: this.form.description || null,
          organizationIds: this.form.organizationIds || [],
        }

        if (this.isEdit) {
          payload.enabled = this.form.enabled
          PublicApiTokenRepository.update(this.id, payload).then(() => {
            this.showLoading(false)
            this.$dialog.notify.success(this.$t('update-success'))
            this.cleanUnsavedChanges()
            this.$router.push({name: 'public-api-tokens'})
          }).catch(err => {
            this.showLoading(false)
            this.notifyError(err)
          })
        } else {
          PublicApiTokenRepository.create(payload).then(response => {
            this.showLoading(false)
            this.cleanUnsavedChanges()
            this.createdToken = response.token
            this.showTokenModal = true
          }).catch(err => {
            this.showLoading(false)
            this.notifyError(err)
          })
        }
      }
    },

    copyToClipboard() {
      navigator.clipboard.writeText(this.createdToken).then(() => {
        this.$dialog.notify.success(this.$t('public-api-token.copied'))
      }).catch(() => {
        this.$dialog.notify.error(this.$t('public-api-token.copy-failed'))
      })
    },

    closeTokenModal() {
      this.showTokenModal = false
      if (this.isRegenerate) {
        this.isRegenerate = false
        this.loadData()
      } else {
        this.$router.push({name: 'public-api-tokens'})
      }
    },

    regenerateToken() {
      this.$dialog.confirm({text: this.$t('public-api-token.confirm-regenerate')}).then(result => {
        if (result) {
          this.showLoading(true)
          PublicApiTokenRepository.regenerate(this.id).then(response => {
            this.showLoading(false)
            this.isRegenerate = true
            this.createdToken = response.token
            this.showTokenModal = true
          }).catch(err => {
            this.showLoading(false)
            this.notifyError(err)
          })
        }
      })
    },

    deleteItem() {
      this.$dialog.confirm({text: this.$t('public-api-token.confirm-delete')}).then(result => {
        if (result) {
          this.showLoading(true)
          PublicApiTokenRepository.delete(this.id).then(() => {
            this.showLoading(false)
            this.$dialog.notify.success(this.$t('delete-success'))
            this.$router.replace({name: 'public-api-tokens'})
          }).catch(err => {
            this.showLoading(false)
            this.notifyError(err)
          })
        }
      })
    }
  }
}
</script>

<style lang="scss">
.public-api-token-form {
  .token-field {
    .v-input__slot {
      font-family: monospace;
    }
  }

  .token-code {
    font-size: 14px;
    font-family: monospace;
    background-color: rgba(0, 0, 0, 0.05);
    padding: 4px 10px;
    border-radius: 4px;
    letter-spacing: 0.5px;
  }
}
</style>
